import _ from 'lodash';
import moment from 'moment';
import Api from 'services/Api';
import store from 'store/index';
import Validator from 'services/Validator';
import {validDate} from 'components/form/validator/validations';
import {isStepValid} from 'services/routingMethods';
import {
    headOfLossTemplate,
    dependantsHeadsOfLossTemplate,
    dependantsCustomHeadsOfLossTemplate,
    carePeriodTemplate,
    lossOfEarningsPeriodTemplate
} from 'store/modules/shared/templates';
import {previousValid} from 'store/generators/step';
import Vue from 'vue';

export const mutationTypes = {
    // Update Values
    UPDATE_VALUE: 'UPDATE_VALUE',

    // Custom Heads of Loss
    ADD_HEAD_OF_LOSS: 'ADD_HEAD_OF_LOSS',
    UPDATE_HEAD_OF_LOSS_VALUE: 'UPDATE_HEAD_OF_LOSS_VALUE',
    DELETE_HEAD_OF_LOSS: 'DELETE_HEAD_OF_LOSS',

    // Past Cost of Care
    ADD_CARE_PERIOD: 'ADD_CARE_PERIOD',
    DELETE_CARE_PERIOD: 'DELETE_CARE_PERIOD',
    UPDATE_CARE_PERIOD_VALUE: 'UPDATE_CARE_PERIOD_VALUE',

    // Past Loss of Earnings
    ADD_LOSS_OF_EARNINGS_PERIOD: 'ADD_LOSS_OF_EARNINGS_PERIOD',
    DELETE_LOSS_OF_EARNINGS_PERIOD: 'DELETE_LOSS_OF_EARNINGS_PERIOD',
    UPDATE_LOSS_OF_EARNINGS_PERIOD_VALUE: 'UPDATE_LOSS_OF_EARNINGS_PERIOD_VALUE',

    // Others
    UPDATE_FROM_SERVER: 'UPDATE_FROM_SERVER',
    UPDATE_TOTAL_INTEREST: 'UPDATE_TOTAL_INTEREST',
    SET: 'SET',
    RESET: 'RESET',
    ADD_DEPENDANTS_HEAD_OF_LOSS: 'ADD_DEPENDANTS_HEAD_OF_LOSS',
    UPDATE_DEPENDANT_HEAD_OF_LOSS_VALUE: 'UPDATE_DEPENDANT_HEAD_OF_LOSS_VALUE',
    UPDATE_DEPENDANT_CUSTOM_HEAD_OF_LOSS_VALUE: 'UPDATE_DEPENDANT_CUSTOM_HEAD_OF_LOSS_VALUE',
    ADD_DEPENDANTS_CUSTOM_HEAD_OF_LOSS: 'ADD_DEPENDANTS_CUSTOM_HEAD_OF_LOSS',
    UPDATE_SUB_TOTAL: 'UPDATE_SUB_TOTAL',
    CLEAR_INTEREST: 'CLEAR_INTEREST'
};

// Initial State
const state = {
    // Bereavement Award
    bereavementAward: 0,
    bereavementAwardInterest: 0.5,
    bereavementAwardWithInterest: 0,

    // ...
    careCalcType: 'own',
    customInterestDate: '',
    dependantsHeadsOfLoss: [],

    // Funeral Expenses
    funeralExpenses: 0,
    funeralExpensesInterest: 0.5,
    funeralExpensesWithInterest: 0,

    // Future Dependency on Income
    futureDependencyOnIncome: 0,

    // Future Dependency on Services
    futureDependencyOnServices: 0,

    // General Damages
    generalDamages: 0,
    generalDamagesInterest: 2.0,
    generalDamagesWithInterest: 0,

    // ...
    gratuitousCare: false,

    // Custom Heads of Loss
    headsOfLoss: [],

    // Calc Interest to when...
    interestType: 'today',

    // Loss of Spouse
    lossOfSpouse: 0,
    lossOfSpouseInterest: 0.5,
    lossOfSpouseWithInterest: 0,

    // Misc Head of Loss
    misc: 0,
    miscInterest: 0.5,
    miscWithInterest: 0,

    // ...
    ownCareFigure: 0,
    ownPastLossOfEarningsFigure: 0,

    // Past Cost of Care
    pastCostOfCare: 0,
    pastCostOfCareInterest: 0.5,
    pastCostOfCareWithInterest: 0,
    pastCostOfTravel: 0,
    pastCostOfTravelInterest: 0.5,
    pastCostOfTravelWithInterest: 0,

    // Past Dependency on Income
    pastDependencyOnIncome: 0,
    pastDependencyOnIncomeInterest: 0.5,
    pastDependencyOnIncomeWithInterest: 0,

    // Past Dependency on Services
    pastDependencyOnServices: 0,
    pastDependencyOnServicesInterest: 0.5,
    pastDependencyOnServicesWithInterest: 0,

    // Past Loss of Earnings
    pastLossOfEarnings: 0,
    pastLossOfEarningsInterest: 0.5,
    pastLossOfEarningsPeriods: [],
    pastLossOfEarningsType: 'own',
    pastLossOfEarningsWithInterest: 0,

    // Needs Renaming
    periods: [],

    // ...
    subTotal: 0,

    // ...
    totalInterest: 0,
};

/**
 * Validation Rules.
 *
 * @type {Object}
 */
const rules = {
    'bereavementAward': {type: 'number', rules: 'required'},
};

/**
 * return number as float
 *
 * @param value
 */
const numberVal = value => parseFloat(value || 0);

/**
 * Check all periods are valid
 *
 * @param state
 */
const checkAllPeriodsValid = (state) => {
    return state.periods.reduce((valid, period) => {
        if (!valid) {
            return valid;
        }
        return moment(period.periodStart, 'DD/MM/YYYY').isValid()
            && moment(period.periodEnd, 'DD/MM/YYYY').isValid()
            && period.hoursPerDay > 0;
    }, true);
};

/**
 * Check all periods are valid
 *
 * @param state
 */
const checkPastLossOfEarningsPeriodsValid = (state) => {
    return state.pastLossOfEarningsPeriods.reduce((valid, period) => {
        if (!valid) {
            return valid;
        }

        let periodStart = moment(period.periodStart, 'DD/MM/YYYY');
        let periodEnd = moment(period.periodEnd, 'DD/MM/YYYY');
        let onsetOfSymptoms = moment( store.getters['fatal/dependency/deceased/theDeceased']['onsetOfSymptoms'] ,'DD/MM/YYYY');

        return periodStart.isValid()
            && periodEnd.isValid()
            && periodStart.isBefore(periodEnd)
            && periodStart.isAfter(onsetOfSymptoms)
            && periodEnd.isAfter(onsetOfSymptoms);
    }, true);
};

/**
 * Get the required interest date
 *
 * @param state
 */
const getInterestDate = (state) => {
    switch (true) {
        case state.interestType === 'today':
            return moment().format('DD/MM/YYYY');
            break;
        case state.interestType === 'assumed_settlement':
            return moment(store.getters['fatal/dependency/claimDetails/value']('assumedSettlement'),
                'DD/MM/YYYY');
            break;
        case state.interestType === 'custom':
            return moment(state.customInterestDate, 'DD/MM/YYYY').format('DD/MM/YYYY');
            break
    }
};

/**
 * Get the validity of the custom heads of loss
 *
 * @param state
 */
const getAllHeadsOfLossValid = (state) => {
    let allValid = true;

    if (typeof state.headsOfLoss !== 'undefined') {
        allValid = state.headsOfLoss.reduce((valid, headOfLoss) => {
            return !valid ? valid : headOfLoss.name !== '' && headOfLoss.value > 0
        }, allValid)
    }

    return allValid;
};

/**
 * Calculate the total Heads of Loss
 *
 * @type {number}
 */
const calcHeadsOfLossTotal = () => {
    let customHeadsOfLossTotal = state.headsOfLoss.reduce((total, customHeadsOfLoss) => total + numberVal(customHeadsOfLoss.value), 0);
    let customHeadsOfLossTotalWithInterest = state.headsOfLoss.reduce((total, customHeadsOfLoss) => total + numberVal(customHeadsOfLoss.withInterest), 0);

    let total = [
        state.generalDamages,
        state.generalDamagesWithInterest,
        state.bereavementAward,
        state.bereavementAwardWithInterest,
        state.funeralExpenses,
        state.funeralExpensesWithInterest,
        state.lossOfSpouse,
        state.lossOfSpouseWithInterest,
        state.pastCostOfTravel,
        state.pastCostOfTravelWithInterest,
        state.pastCostOfCare,
        state.pastCostOfCareWithInterest,
        state.pastLossOfEarnings,
        state.pastLossOfEarningsWithInterest,
        state.misc,
        state.miscWithInterest,
        state.pastDependencyOnIncome,
        state.pastDependencyOnIncomeWithInterest,
        state.pastDependencyOnServices,
        state.pastDependencyOnServicesWithInterest,
        customHeadsOfLossTotal,
        customHeadsOfLossTotalWithInterest,
    ].reduce((total, value) => total + parseFloat(value, 10), 0);

    return numberVal(total).toFixed(2);
};

/**
 * Calculate the total Heads of Loss without interest
 *
 * @type {number}
 */
const calcHeadsOfLossTotalMinusInterest = () => {
    let customHeadsOfLossTotal = state.headsOfLoss.reduce((total, customHeadsOfLoss) => total + numberVal(customHeadsOfLoss.value), 0);

    let total = [
        state.generalDamages,
        state.bereavementAward,
        state.funeralExpenses,
        state.lossOfSpouse,
        state.pastCostOfTravel,
        state.pastCostOfCare,
        state.pastLossOfEarnings,
        state.misc,
        state.pastDependencyOnIncome,
        state.pastDependencyOnServices,
        customHeadsOfLossTotal
    ].reduce((total, value) => total + parseFloat(value, 10), 0);

    return numberVal(total).toFixed(2);
};

/**
 * Return the payload required for the interest calculation modals
 *
 * @type {object}
 */
const modalPayload = () => {
    return {
        interimPayments: store.getters['fatal/dependency/interimPayments/interimPayments'],
        assumedSettlement: store.getters['fatal/dependency/claimDetails/value']('assumedSettlement'),
        serviceOfProceedings: store.getters['fatal/dependency/claimDetails/value']('serviceOfProceedings'),
        onsetOfSymptoms: store.getters['fatal/dependency/deceased/theDeceased']['onsetOfSymptoms'],
        assumedLifeExpectancy: store.getters['fatal/dependency/deceased/theDeceased']['assumedLifeExpectancy'],
        deductibles: store.getters['fatal/dependency/cru/all'],
        headsOfLoss: state,
        headOfLoss: {}
    }
};

// Getters
const getters = {
    getAll: state => state,
    value: state => key => state[key],
    getModalPayload: state => modalPayload(),
    findHeadOfLoss: state => index => state.headsOfLoss[index],
    headsOfLoss: state => state.headsOfLoss,
    findDependantsHeadsOfLoss: state => index => state.dependantsHeadsOfLoss[index],
    allHeadsOfLossValid: state => getAllHeadsOfLossValid(state),
    findPeriod: state => index => state.periods[index],
    findLossOfEarningsPeriod: state => index => state.pastLossOfEarningsPeriods[index],
    periods: state => state.periods,
    allPeriodsValid: state => checkAllPeriodsValid(state),
    pastLossOfEarningsPeriods: state => state.pastLossOfEarningsPeriods,
    pastLossOfEarningsPeriodsValid: state => checkPastLossOfEarningsPeriodsValid(state),
    totalInterest: state => state.totalInterest,
    interestDate: state => getInterestDate(state),
    getHeadsOfLossTotal: state => calcHeadsOfLossTotal(),
    getHeadsOfLossTotalMinusInterest: state => calcHeadsOfLossTotalMinusInterest(),
};

// Actions
const actions = {
    /**
     * Save the step by submitting to the server.
     *
     * @param dispatch
     * @param payload
     */
    save({dispatch}, payload) {
        // We might change this to something which
        // is a bit cleaner...
        payload.data = state;

        Api.claims.save(payload).then((response) => {
            return response;
        });
    },

    /**
     * Loads the step being received from the Server, to be placed in the state.
     *
     * Merges the existing state, which should be clean, with the new data before
     * commiting the mutation. This allows legacy state loaded from the server
     * to receive new/changed observable properties without breaking things!
     *
     * @param commit
     * @param step
     */
    load({commit}, step) {
        const data = _.deeply(_.mapKeys)(step, (value, key) => _.camelCase(key)).data;

        commit(mutationTypes.UPDATE_FROM_SERVER, _.merge(state, data));
        commit(mutationTypes.SET, { key: 'periods', value: data.periods });
        commit(mutationTypes.SET, { key: 'pastLossOfEarningsPeriods', value: data.pastLossOfEarningsPeriods });
    },

    /**
     * Performs any processing once the data from the server has been stored.
     * This is a good place to do calculations or dispatch other actions which
     * are used to refresh/update calculated data on other steps/components.
     */
    process({rootState, dispatch}) {
        store.dispatch('fatal/dependency/step/setStepNumberValid', {
            stepNo: 5,
            valid: previousValid(rootState, 5) && (new Validator(state, rules)).validate(),
        });

        dispatch('determineBereavementAward');
    },

    /**
     * Validate the step.
     *
     * @param dispatch
     * @param rootState
     */
    validate({dispatch, rootState}) {
        store.dispatch('fatal/dependency/step/setStepNumberValid', {
            stepNo: 5,
            valid: previousValid(rootState, 5) && (new Validator(state, rules)).validate(),
        }).then(() => dispatch('determineBereavementAward'));
    },

    /**
     * Resets the step back to defaults
     *
     * @param commit
     */
    reset({commit}) {
        store.dispatch('fatal/dependency/step/setStepNumberValid', {
            stepNo: 5,
            valid: false,
        });

        commit(mutationTypes.RESET);
    },

    /**
     * Update heads of loss
     *
     * @param commit
     * @param state
     * @param dispatch
     * @param key
     * @param value
     */
    updateValue({commit, state, dispatch}, {key, value}) {
        if (state[key] === value) {
            return value;
        }
        commit(mutationTypes.UPDATE_VALUE, {key, value});
        dispatch('updateSubTotal');
        return value;
    },

    /**
     * Update heads of loss value
     *
     * @param commit
     * @param dispatch
     * @param data
     */
    updateValues({commit, dispatch}, data) {
        Object.keys(data).forEach((key) => {
            if (key.includes("custom_") ) {
                commit(mutationTypes.UPDATE_HEAD_OF_LOSS_VALUE, {
                    key: data[key]['key'],
                    value: data[key]['value'],
                    index: data[key]['index']
                });
            }
            commit(mutationTypes.UPDATE_VALUE, {key: key+'WithInterest', value: data[key]});
            dispatch('updateTotalInterest');
            dispatch('updateSubTotal');
        });
    },

    /**
     * Update the sub total
     *
     * @param commit
     * @param commit
     * @param rootGetters
     */
    updateSubTotal({state, commit, rootGetters}) {
        const subTotal = numberVal(state.generalDamages) +
            numberVal(state.bereavementAward) +
            numberVal(state.funeralExpenses) +
            numberVal(state.lossOfSpouse) +
            numberVal(state.pastLossOfEarnings) +
            numberVal(state.pastCostOfTravel) +
            numberVal(state.pastCostOfCare) +
            numberVal(state.misc) +
            state.headsOfLoss.reduce(function (total, customHead) {
                return total + numberVal(customHead.value);
            }, 0) +
            numberVal(rootGetters['fatal/dependency/dependencyOnServices/preDependencyValue']) +
            numberVal(rootGetters['fatal/dependency/dependencyOnIncome/preDependencyValue']);
        commit(mutationTypes.UPDATE_SUB_TOTAL, numberVal(subTotal));
    },

    /**
     * Update total interest
     *
     * @param state
     * @param commit
     */
    updateTotalInterest({state, commit}) {
        const total = numberVal(state.generalDamagesWithInterest) +
            numberVal(state.pastLossOfEarningsWithInterest) +
            numberVal(state.pastCostOfCareWithInterest) +
            numberVal(state.pastCostOfTravelWithInterest);
        commit(mutationTypes.UPDATE_TOTAL_INTEREST, total);
    },

    /**
     * Add a head of loss
     *
     * @param commit
     */
    addHeadOfLoss({commit}) {
        commit(mutationTypes.ADD_HEAD_OF_LOSS);
    },

    /**
     * Add a dependant head of loss
     *
     * @param commit
     */
    addDependantHeadOfLoss({commit}) {
        commit(mutationTypes.ADD_DEPENDANTS_HEAD_OF_LOSS);
    },

    /**
     * Delete a dependant head of loss
     *
     * @param commit
     * @param index
     */
    deleteHeadOfLoss({commit}, index) {
        commit(mutationTypes.DELETE_HEAD_OF_LOSS, index);
    },

    /**
     * Add a period
     *
     * @param commit
     */
    addCarePeriod({commit}) {
        commit(mutationTypes.ADD_CARE_PERIOD);
    },

    /**
     * Add a period
     *
     * @param commit
     */
    addLossOfEarningsPeriod({commit}) {
        commit(mutationTypes.ADD_LOSS_OF_EARNINGS_PERIOD);
    },

    /**
     * Delete a period
     *
     * @param commit
     * @param dispatch
     * @param index
     */
    deleteCarePeriod({commit, dispatch}, index) {
        commit(mutationTypes.DELETE_CARE_PERIOD, index);
        return dispatch('updateCareTotal');
    },

    /**
     * Delete a period
     *
     * @param commit
     * @param dispatch
     * @param index
     */
    deleteLossOfEarningsPeriod({commit, dispatch}, index) {
        commit(mutationTypes.DELETE_LOSS_OF_EARNINGS_PERIOD, index);
        return dispatch('updatePastLossOfEarningsTotal');
    },

    /**
     * Update a period in full.
     *
     * This is only called when a server updates, should change name.
     *
     *
     * @param commit
     * @param dispatch
     * @param care
     * @param index
     */
    updatePeriod({dispatch, commit}, {care, index}) {
        Object.keys(care[0])
            .filter(key => key !== 'periodStart' && key !== 'periodEnd')
            .forEach((key) => {
                const value = care[0][key];
                commit(mutationTypes.UPDATE_CARE_PERIOD_VALUE, {key, value, index});
            });
        dispatch('updateCareTotal');
    },

    /**
     * Update a period value
     *
     * @param commit
     * @param state
     * @param key
     * @param value
     * @param index
     */
    updatePeriodValue({commit, state}, {key, value, index}) {
        // Prevent there being more than 24 hours in a day. Min/max attributes are buggy.
        if (key === 'hoursPerDay' && value > 24) {
            value = 24;
        }

        if (state.periods[index][key] === value) {
            return value;
        }
        commit(mutationTypes.UPDATE_CARE_PERIOD_VALUE, {key, value, index});
        return value;
    },

    /**
     * Update a period value
     *
     * @param commit
     * @param state
     * @param key
     * @param value
     * @param index
     */
    updatePastLossOfEarningsPeriodValue({commit, state}, {key, value, index}) {
        if (state.pastLossOfEarningsPeriods[index][key] === value) {
            return value;
        }
        commit(mutationTypes.UPDATE_LOSS_OF_EARNINGS_PERIOD_VALUE, {key, value, index});
        return value;
    },

    /**
     * Update a head of loss value
     *
     * @param commit
     * @param state
     * @param key
     * @param value
     * @param index
     */
    updateHeadOfLossValue({commit, state}, {key, value, index}) {
        if (state.headsOfLoss[index][key] === value) {
            return value;
        }
        commit(mutationTypes.UPDATE_HEAD_OF_LOSS_VALUE, {key, value, index});
        return value;
    },

    /**
     * Update a dependant head of loss value
     *
     * @param commit
     * @param state
     * @param key
     * @param value
     * @param index
     */
    updateDependantHeadOfLossValue({commit, state}, {key, value, index}) {
        if (state.dependantsHeadsOfLoss[index][key] === value) {
            return value;
        }
        commit(mutationTypes.UPDATE_DEPENDANT_HEAD_OF_LOSS_VALUE, {key, value, index});
        return value;
    },

    /**
     * Update a dependant custom head of loss value
     *
     * @param commit
     * @param state
     * @param key
     * @param value
     * @param index
     * @param comparisonIndex
     */
    updateDependantCustomHeadOfLossValue({commit, state}, {key, value, index, comparisonIndex}) {
        if (state.dependantsCustomHeadsOfLoss == 'undefined') {
            commit(mutationTypes.ADD_DEPENDANTS_CUSTOM_HEAD_OF_LOSS, {comparisonIndex});
        }
        commit(mutationTypes.UPDATE_DEPENDANT_CUSTOM_HEAD_OF_LOSS_VALUE, {key, value, index});
        return value;
    },

    /**
     * Calculate the Bereavement Award
     *
     * @param dispatch
     * @param rootGetters
     */
    determineBereavementAward({dispatch, rootGetters}) {
        const mDod = moment(rootGetters['fatal/dependency/deceased/theDeceased']['dateOfDeath'], 'DD/MM/YYYY');
        if (!mDod.isValid()) {
            return;
        }
        if (mDod.isAfter('2020-04-30')) {
            return dispatch('updateValue', {key: 'bereavementAward', value: 15120});
        }
        if (mDod.isAfter('2013-03-21')) {
            return dispatch('updateValue', {key: 'bereavementAward', value: 12980});
        }
        if (mDod.isAfter('2008-01-01')) {
            return dispatch('updateValue', {key: 'bereavementAward', value: 11800});
        }
        return dispatch('updateValue', {key: 'bereavementAward', value: 10000});
    },

    /**
     * Calculate all the interest
     *
     * @param dispatch
     * @param state
     */
    calcAllInterest({dispatch, state}) {
        if (state.interestType === 'none' ) { dispatch('clearInterest'); return; }
        if (state.interestType === 'custom' && state.customInterestDate === '' ) { return; }
        const dateOfDeath = store.getters['fatal/dependency/deceased/theDeceased']['dateOfDeath'];

        if (!dateOfDeath || !isStepValid(2) || !isStepValid(6) || !isStepValid(8)) {
            return;
        }

        return Api.calculators.monetary.interest(
            store.getters['fatal/dependency/claimDetails/value']('proceduralStatus'),
            store.getters['fatal/dependency/claimDetails/value']('assumedSettlement'),
            store.getters['fatal/dependency/claimDetails/value']('serviceOfProceedings'),
            store.getters['fatal/dependency/deceased/theDeceased']['onsetOfSymptoms'],
            store.getters['fatal/dependency/deceased/theDeceased']['assumedLifeExpectancy'],
            store.getters['fatal/dependency/cru/all'],
            state,
            store.getters['fatal/dependency/interimPayments/interimPayments'],
        ).then(data => dispatch('updateValues', data));
    },

    /**
     * Calculate the care sub totals for the period
     *
     * @param dispatch
     * @param state
     * @param index
     */
    calcCareSubTotalForPeriod: _.debounce(({dispatch, state}, index) => {
        if (validDate(state.periods[index].periodStart) && validDate(state.periods[index].periodEnd) && state.periods[index].hoursPerDay > 0) {
            return Api.calculators.monetary.detailedCare([state.periods[index]], state.gratuitousCare)
                .then(care => dispatch('updatePeriod', {care, index}))
                .then(() => dispatch('updateCareTotal'));
        }
    }, 500),

    /**
     * Calculate the loss of earnings sub totals for the period
     *
     * @param dispatch
     * @param state
     * @param index
     */
    calcLossOfEarningsForPeriod: _.debounce(({dispatch, state, rootState}, index) => {
        if (validDate(state.pastLossOfEarningsPeriods[index].periodStart)
         && validDate(state.pastLossOfEarningsPeriods[index].periodEnd)
         && state.pastLossOfEarningsPeriods[index].periodValue
        ) {
            let period = state.pastLossOfEarningsPeriods[index];
            let person = rootState.fatal.dependency.deceased.theDeceased;

            return Api.calculators.monetary.pastLossOfEarnings({period, person})
                .then(result => {
                    dispatch('updatePastLossOfEarningsPeriodValue', {key: 'periodTotal', value: result.period_total, index})
                })
                .then(() => dispatch('updatePastLossOfEarningsTotal'));
        }
    }, 500),

    /**
     * Update the care total
     *
     * @param commit
     * @param state
     */
    updateCareTotal({commit, state}) {
        const value = state.periods.reduce((total, period) => total + numberVal(period.total), 0);
        commit(mutationTypes.UPDATE_VALUE, {key: 'pastCostOfCare', value: value.toFixed(2)});
    },

    /**
     * Update the past loss of earnings total total
     *
     * @param commit
     * @param state
     */
    updatePastLossOfEarningsTotal({commit, state}) {
        const value = state.pastLossOfEarningsPeriods.reduce((total, period) => total + numberVal(period.periodTotal), 0);
        commit(mutationTypes.UPDATE_VALUE, {key: 'pastLossOfEarnings', value: value.toFixed(2)});
    },

    /**
     * Calculate care
     *
     * @param commit
     * @param state
     * @param value
     */
    ownCalculateCare({commit, state}, value) {
        let discountedCare = 0;
        if (value) {
            discountedCare = (state.ownCareFigure * 0.75).toFixed(2);
            commit(mutationTypes.UPDATE_VALUE, {key: 'pastCostOfCare', value: discountedCare});
        } else {
            commit(mutationTypes.UPDATE_VALUE, {key: 'pastCostOfCare', value: state.ownCareFigure});
        }
    },

    /**
     * Auto calculate care
     *
     * @param commit
     * @param dispatch
     * @param value
     */
    autoCalculateCare({commit, dispatch}, value) {
        commit(mutationTypes.UPDATE_VALUE, {key: 'periods', value: []});
        commit(mutationTypes.UPDATE_VALUE, {key: 'ownCareFigure', value: 0});
        return Api.calculators.monetary.autoCalculateCare(value)
            .then(care => dispatch('updateValue', {key: 'pastCostOfCare', value: care.value}));
    },

    /**
     * Save comparison schedule
     *
     * @param rootGetters
     * @param dispatch
     */
    saveComparisonSchedule({rootGetters, dispatch}) {
        const data = rootGetters['fatal/dependency/dataForStep5'];
        return Api.claims.save(data).then((data) => {
            dispatch('recordServerResponse', data);
            // Handler server errors
            return data;
        });
    },

    /**
     * Update past dependency on income
     *
     * @param state
     * @param commit
     * @param rootGetters
     */
    updatePastDependencyOnIncome({state, commit, rootGetters}) {
        const pastDependencyOnIncome = rootGetters['fatal/dependency/dependencyOnIncome/preDependencyValue'];
        if (pastDependencyOnIncome) {
            commit(mutationTypes.UPDATE_VALUE, {
                key: 'pastDependencyOnIncome',
                value: numberVal(pastDependencyOnIncome.toFixed(2))
            });
        }
    },

    /**
     * Update past dependency on services
     *
     * @param commit
     * @param rootGetters
     */
    updatePastDependencyOnServices({commit, rootGetters}) {
        const pastDependencyOnServices = rootGetters['fatal/dependency/dependencyOnServices/preDependencyValue'];
        commit(mutationTypes.UPDATE_VALUE, {
            key: 'pastDependencyOnServices', value: numberVal(pastDependencyOnServices.toFixed(2))
        });
    },
};

// Mutations
const mutations = {
    [mutationTypes.UPDATE_VALUE](state, {key, value}) {
        state[key] = value;
    },
    [mutationTypes.ADD_HEAD_OF_LOSS](state) {
        state.headsOfLoss.push(headOfLossTemplate());
    },
    [mutationTypes.ADD_DEPENDANTS_HEAD_OF_LOSS](state) {
        state.dependantsHeadsOfLoss.push(dependantsHeadsOfLossTemplate());
    },
    [mutationTypes.ADD_DEPENDANTS_CUSTOM_HEAD_OF_LOSS](state, {comparisonIndex}) {
        state.dependantsHeadsOfLoss[comparisonIndex].dependantsCustomHeadsOfLoss.push(dependantsCustomHeadsOfLossTemplate());
    },
    [mutationTypes.DELETE_HEAD_OF_LOSS](state, index) {
        if (index === 0 && state.headsOfLoss.length === 1) {
            state.headsOfLoss = [];
        }
        state.headsOfLoss.splice(index, 1);
    },
    [mutationTypes.ADD_CARE_PERIOD](state) {
        state.periods.push(carePeriodTemplate());
    },
    [mutationTypes.DELETE_CARE_PERIOD](state, index) {
        state.periods.splice(index, 1);
    },
    [mutationTypes.UPDATE_CARE_PERIOD_VALUE](state, {key, value, index}) {
        state.periods[index][key] = value;
    },
    [mutationTypes.ADD_LOSS_OF_EARNINGS_PERIOD](state) {
        state.pastLossOfEarningsPeriods.push(lossOfEarningsPeriodTemplate());
    },
    [mutationTypes.DELETE_LOSS_OF_EARNINGS_PERIOD](state, index) {
        state.pastLossOfEarningsPeriods.splice(index, 1);
    },
    [mutationTypes.UPDATE_LOSS_OF_EARNINGS_PERIOD_VALUE](state, {key, value, index}) {
        state.pastLossOfEarningsPeriods[index][key] = value;
    },
    [mutationTypes.SET](state, { key, value }) {
        Vue.set(state, key, value);
    },
    [mutationTypes.UPDATE_FROM_SERVER](state, data) {
        state.bereavementAward = data.bereavementAward;
        state.bereavementAwardInterest = data.bereavementAwardInterest;
        state.bereavementAwardWithInterest = data.bereavementAwardWithInterest;
        state.careCalcType = data.careCalcType;
        state.customInterestDate = data.customInterestDate;
        state.dependantsHeadsOfLoss = data.dependantsHeadsOfLoss;
        state.funeralExpenses = data.funeralExpenses;
        state.funeralExpensesInterest = data.funeralExpensesInterest;
        state.funeralExpensesWithInterest = data.funeralExpensesWithInterest;
        state.generalDamages = data.generalDamages;
        state.generalDamagesInterest = data.generalDamagesInterest;
        state.generalDamagesWithInterest = data.generalDamagesWithInterest;
        state.gratuitousCare = data.gratuitousCare;
        state.headsOfLoss = data.headsOfLoss;
        state.interestType = data.interestType;
        state.lossOfSpouse = data.lossOfSpouse;
        state.lossOfSpouseInterest = data.lossOfSpouseInterest;
        state.lossOfSpouseWithInterest = data.lossOfSpouseWithInterest;
        state.misc = data.misc;
        state.miscInterest = data.miscInterest;
        state.miscWithInterest = data.miscWithInterest;
        state.ownCareFigure = data.ownCareFigure;
        state.pastCostOfCare = data.pastCostOfCare;
        state.pastCostOfCareInterest = data.pastCostOfCareInterest;
        state.pastCostOfCareWithInterest = data.pastCostOfCareWithInterest;
        state.pastCostOfTravel = data.pastCostOfTravel;
        state.pastCostOfTravelInterest = data.pastCostOfTravelInterest;
        state.pastCostOfTravelWithInterest = data.pastCostOfTravelWithInterest;
        state.pastDependencyOnIncome = data.pastDependencyOnIncome;
        state.pastDependencyOnIncomeInterest = data.pastDependencyOnIncomeInterest;
        state.pastDependencyOnIncomeWithInterest = data.pastDependencyOnIncomeWithInterest;
        state.pastDependencyOnServices = data.pastDependencyOnServices;
        state.pastDependencyOnServicesInterest = data.pastDependencyOnServicesInterest;
        state.pastDependencyOnServicesWithInterest = data.pastDependencyOnServicesWithInterest;
        state.pastLossOfEarnings = data.pastLossOfEarnings;
        state.pastLossOfEarningsPeriods = data.pastLossOfEarningsPeriods;
        state.pastLossOfEarningsInterest = data.pastLossOfEarningsInterest;
        state.pastLossOfEarningsType = data.pastLossOfEarningsType;
        state.pastLossOfEarningsWithInterest = data.pastLossOfEarningsWithInterest;
        state.periods = data.periods;
        state.subTotal = data.subTotal;
        state.totalInterest = data.totalInterest;
    },
    [mutationTypes.UPDATE_TOTAL_INTEREST](state, value) {
        state.totalInterest = value;
    },
    [mutationTypes.UPDATE_HEAD_OF_LOSS_VALUE](state, {key, value, index}) {
        state.headsOfLoss[index][key] = value;
    },
    [mutationTypes.UPDATE_DEPENDANT_HEAD_OF_LOSS_VALUE](state, {key, value, index}) {
        state.dependantsHeadsOfLoss[index][key] = value;
    },
    [mutationTypes.UPDATE_DEPENDANT_CUSTOM_HEAD_OF_LOSS_VALUE](state, {key, value, index}) {
        state.dependantsCustomHeadsOfLoss[index][key] = value;
    },
    [mutationTypes.UPDATE_SUB_TOTAL](state, subTotal) {
        state.subTotal = subTotal;
    },
    [mutationTypes.RESET](state) {
        state.bereavementAward = 0;
        state.bereavementAwardInterest = 0.5;
        state.bereavementAwardWithInterest = 0;
        state.careCalcType = 'own';
        state.customInterestDate = '';
        state.dependantsHeadsOfLoss = [];
        state.funeralExpenses = 0;
        state.funeralExpensesInterest = 0.5;
        state.funeralExpensesWithInterest = 0;
        state.futureDependencyOnIncome = 0;
        state.futureDependencyOnServices = 0;
        state.generalDamages = 0;
        state.generalDamagesInterest = 2.0;
        state.generalDamagesWithInterest = 0;
        state.gratuitousCare = false;
        state.headsOfLoss = [];
        state.interestType = 'today';
        state.lossOfSpouse = 0;
        state.lossOfSpouseInterest = 0.5;
        state.lossOfSpouseWithInterest = 0;
        state.misc = 0;
        state.miscInterest = 0.5;
        state.miscWithInterest = 0;
        state.ownCareFigure = 0;
        state.ownPastLossOfEarningsFigure = 0;
        state.pastCostOfCare = 0;
        state.pastCostOfCareInterest = 0.5;
        state.pastCostOfCareWithInterest = 0;
        state.pastCostOfTravel = 0;
        state.pastCostOfTravelInterest = 0.5;
        state.pastCostOfTravelWithInterest = 0;
        state.pastDependencyOnIncome = 0;
        state.pastDependencyOnIncomeInterest = 0.5;
        state.pastDependencyOnIncomeWithInterest = 0;
        state.pastDependencyOnServices = 0;
        state.pastDependencyOnServicesInterest = 0.5;
        state.pastDependencyOnServicesWithInterest = 0;
        state.pastLossOfEarnings = 0;
        state.pastLossOfEarningsInterest = 0.5;
        state.pastLossOfEarningsPeriods = [];
        state.pastLossOfEarningsType = 'own';
        state.pastLossOfEarningsWithInterest = 0;
        state.periods = [];
        state.subTotal = 0;
        state.totalInterest = 0;
    },
    [mutationTypes.CLEAR_INTEREST](state) {
        state.bereavementAwardWithInterest = 0;
        state.funeralExpensesWithInterest = 0;
        state.generalDamagesWithInterest = 0;
        state.lossOfSpouseWithInterest = 0;
        state.miscWithInterest = 0;
        state.pastCostOfCareWithInterest = 0;
        state.pastCostOfTravelWithInterest = 0;
        state.pastDependencyOnIncomeWithInterest = 0;
        state.pastDependencyOnServicesWithInterest = 0;
        state.pastLossOfEarningsWithInterest = 0;
        state.totalInterest = 0;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/fatal/dependency/headsOfLoss.js